import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Home, Plus, Settings, User, Sparkles, Ellipsis } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { AnimatePresence, motion } from 'framer-motion';

interface MobileBottomNavProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onAddRecipe: () => void;
  onOpenStudio?: () => void;
  isAdmin: boolean;
}

interface TabButtonProps {
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  onClick: () => void;
  hasIndicator?: boolean;
}

const TabButton: React.FC<TabButtonProps> = ({ icon: Icon, label, isActive, onClick, hasIndicator }) => (
  <button
    onClick={onClick}
    className={`relative flex flex-col items-center justify-center p-2 min-w-[60px] transition-colors ${isActive
        ? 'text-warmth-600'
        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
  >
    <Icon className={`h-5 w-5 mb-1 ${isActive ? 'fill-current' : ''}`} />
    <span className="text-xs font-medium">{label}</span>
    {hasIndicator && (
      <span className="absolute top-1 right-2 md:right-3 block h-2 w-2 rounded-full bg-warmth-500 ring-1 ring-white dark:ring-gray-900" />
    )}
  </button>
);

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  activeTab,
  onTabChange,
  onAddRecipe,
  onOpenStudio,
  isAdmin
}) => {
  const { role } = useAuth();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const isManageOrProfileActive = activeTab === 'manage' || activeTab === 'profile';

  return (
    <>
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 pb-safe">
        <div className="glass-nav border-t border-gray-200 dark:border-gray-700 px-2 py-1.5 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl">
          <div className="flex items-center justify-around relative">
            <TabButton
              icon={Home}
              label="Home"
              isActive={activeTab === 'home'}
              onClick={() => onTabChange('home')}
            />

            <TabButton
              icon={Plus}
              label="Add"
              isActive={activeTab === 'add'}
              onClick={onAddRecipe}
            />

            <div className="flex flex-col items-center -mt-8 relative z-50">
              <Button
                size="icon"
                className="h-14 w-14 rounded-2xl shadow-xl shadow-terracotta-500/20 bg-terracotta-500 text-white border-0 transition-transform active:scale-90"
                onClick={onOpenStudio || (() => { })}
              >
                <Sparkles className="h-7 w-7" />
              </Button>
              <span className="text-[10px] font-bold text-terracotta-600 dark:text-terracotta-400 mt-1 uppercase tracking-wider">Studio</span>
            </div>

            <TabButton
              icon={activeTab === 'manage' ? Settings : User}
              label={isAdmin ? "Manage" : "Profile"}
              isActive={isManageOrProfileActive}
              onClick={() => setIsMoreMenuOpen(true)}
            />

            <TabButton
              icon={Ellipsis}
              label="More"
              isActive={false}
              onClick={() => setIsMoreMenuOpen(true)}
            />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMoreMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMoreMenuOpen(false)}
              className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm md:hidden"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: "0%" }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="md:hidden fixed bottom-0 left-0 right-0 z-40 pb-safe"
            >
              <div className="glass-nav p-4 bg-white/95 dark:bg-gray-900/95 border-t border-gray-200 dark:border-gray-800 rounded-t-2xl">
                <div className="w-10 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-4"></div>
                <div className="space-y-2">
                  {(isAdmin || role === 'reader') ? (
                    <button
                      onClick={() => {
                        onTabChange('manage');
                        setIsMoreMenuOpen(false);
                      }}
                      className="w-full flex items-center p-3 rounded-lg text-left text-base font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <Settings className="h-5 w-5 mr-4" />
                      Manage
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        onTabChange('profile');
                        setIsMoreMenuOpen(false);
                      }}
                      className="w-full flex items-center p-3 rounded-lg text-left text-base font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <User className="h-5 w-5 mr-4" />
                      Profile
                    </button>
                  )}

                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileBottomNav;
